import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import './StripePages.css';
import Swal from 'sweetalert2';
import {
  ensureFirebaseApp,
  getPathWithoutReturnToken,
  getReturnToken,
} from '../utils/mobileWebAuth';

function OnboardingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [onboardingUrl, setOnboardingUrl] = useState(null);
  const source = searchParams.get('source') === 'mobile' ? 'mobile' : 'web';
  const appReturnUrl = searchParams.get('app_return') || 'fodyfee://stripe/return';
  const returnToken = getReturnToken(searchParams);
  const canUseMobileGuestFlow = source === 'mobile' && !!returnToken;
  const mobileQuery = `source=mobile&app_return=${encodeURIComponent(appReturnUrl)}`;
  const plansPath = source === 'mobile' ? `/admin/plans?${mobileQuery}` : '/admin/plans';
  const otpPath = source === 'mobile' ? `/admin/signup/otp?${mobileQuery}` : '/admin/signup/otp';

  useEffect(() => {
    ensureFirebaseApp();
    const auth = getAuth();

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        runOnboardingFlow(currentUser.uid, currentUser.email || '', currentUser.displayName || '');
      } else if (canUseMobileGuestFlow) {
        // No Firebase session in this external mobile browser — identify via
        // the signed return token instead of trusting a raw userId.
        runMobileGuestOnboardingFlow(returnToken);
      } else {
        localStorage.setItem('redirectAfterLogin', getPathWithoutReturnToken(searchParams));
        navigate('/admin/login');
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, source, appReturnUrl, returnToken, canUseMobileGuestFlow]);

  const runMobileGuestOnboardingFlow = async (token) => {
    try {
      const functions = getFunctions();
      const getStripeAccountStatus = httpsCallable(functions, 'getStripeAccountStatus');
      const result = await getStripeAccountStatus({ returnToken: token });
      const data = result.data || {};

      const onboardingComplete =
        data.success &&
        data.connected &&
        data.chargesEnabled &&
        data.payoutsEnabled &&
        data.detailsSubmitted;

      if (onboardingComplete) {
        navigate(plansPath);
        return;
      }

      if (data.success && data.connected) {
        await generateAccountLink({ token });
        return;
      }

      Swal.fire(
        'Stripe account required',
        'We could not find your Stripe account in this web session. Please return to the app and start setup again.',
        'warning'
      );
      setLoading(false);
    } catch (error) {
      console.error('[Onboarding] Mobile guest flow error:', error);
      Swal.fire('Error', error.message || 'Failed to continue onboarding.', 'error');
      setLoading(false);
    }
  };

  /**
   * Main onboarding orchestrator:
   * 1. Check Firestore status
   * 2. If no Stripe account → create one automatically
   * 3. If onboarding incomplete → generate Account Link → redirect to Stripe
   * 4. If onboarding complete → redirect to Plans
   */
  const runOnboardingFlow = async (uid, email, displayName) => {
    try {
      const db = getFirestore();
      const userDoc = await getDoc(doc(db, 'users', uid));
      const userData = userDoc.data() || {};

      const otpVerified = userData?.signupOtpVerified;
      const otpRequired = userData?.signupOtpRequired === true || otpVerified === false;
      if (otpRequired && otpVerified !== true) {
        navigate(otpPath);
        return;
      }

      const stripeAccountId = userData?.stripeAccountId;
      const onboardingComplete =
        userData?.stripeOnboardingComplete === true ||
        (userData?.stripeChargesEnabled === true &&
          userData?.stripePayoutsEnabled === true &&
          userData?.stripeDetailsSubmitted === true);

      // Step 1: Onboarding already complete → go to Plans
      if (stripeAccountId && onboardingComplete) {
        console.log('[Onboarding] Already complete — redirecting to Plans');
        navigate(plansPath);
        return;
      }

      // Step 1b: Account exists but Firestore says incomplete — double-check with Stripe
      if (stripeAccountId && !onboardingComplete) {
        console.log('[Onboarding] Firestore says incomplete — double-checking with Stripe API...');
        try {
          const functions = getFunctions();
          const getStripeAccountStatus = httpsCallable(functions, 'getStripeAccountStatus');
          const result = await getStripeAccountStatus({ userId: uid });
          const data = result.data;
          if (data.success && data.connected && data.chargesEnabled && data.payoutsEnabled && data.detailsSubmitted) {
            console.log('[Onboarding] Stripe API says complete — syncing to Firestore and redirecting to Plans');
            // Sync the real status to Firestore so ProtectedRoute sees it next time
            await setDoc(doc(db, 'users', uid), {
              stripeOnboardingComplete: true,
              stripeChargesEnabled: data.chargesEnabled,
              stripePayoutsEnabled: data.payoutsEnabled,
              stripeDetailsSubmitted: data.detailsSubmitted,
            }, { merge: true });
            navigate(plansPath);
            return;
          }
        } catch (e) {
          console.warn('[Onboarding] Double-check failed, continuing with Account Link', e);
        }
      }

      // Step 2: No Stripe account → create one automatically
      if (!stripeAccountId) {
        console.log('[Onboarding] No Stripe account — creating one...');
        await createStripeAccount(uid, email, displayName, userData);
      }

      // Step 3: Generate Account Link and redirect to Stripe
      console.log('[Onboarding] Generating Account Link...');
      await generateAccountLink({ uid });
    } catch (error) {
      console.error('[Onboarding] Flow error:', error);
      Swal.fire('Error', error.message || 'Failed to start onboarding. Please try again.', 'error');
      setLoading(false);
    }
  };

  const createStripeAccount = async (uid, email, displayName, userData) => {
    try {
      const functions = getFunctions();
      const createStripeAccountFn = httpsCallable(functions, 'createStripeAccount');

      const result = await createStripeAccountFn({
        email: email,
        businessName: displayName || userData?.name || 'Business',
        userId: uid,
        businessType: 'individual',
        phone: userData?.phone || '',
        address: userData?.stripeBusinessAddress || '',
        baseUrl: window.location.origin,
      });

      const data = result.data;
      if (!data.success) {
        throw new Error(data.error || 'Failed to create Stripe account');
      }

      console.log('[Onboarding] Stripe account created:', data.accountId);
    } catch (error) {
      console.error('[Onboarding] createStripeAccount error:', error);
      throw error;
    }
  };

  const generateAccountLink = async ({ uid, token }) => {
    try {
      const functions = getFunctions();
      const createStripeAccountLink = httpsCallable(functions, 'createStripeAccountLink');
      const result = await createStripeAccountLink({
        userId: uid,
        source,
        appReturnUrl,
        // Only meaningful (and only sent) when there's no real Firebase
        // session in this browser — see createStripeAccountLink/
        // resolveUidOrReturnToken on the backend.
        ...(token ? { returnToken: token } : {}),
      });
      const data = result.data;

      if (data.success && data.dashboard) {
        // Account is already fully verified, go to plans
        navigate(plansPath);
      } else if (data.success && data.url) {
        setOnboardingUrl(data.url);
        // Auto-redirect to Stripe hosted onboarding
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Failed to generate onboarding link');
      }
    } catch (error) {
      console.error('[Onboarding] generateAccountLink error:', error);
      throw error;
    }
  };

  const floatingIngredients = (
    <div className="ff-hero-ingredients">
      <div className="ff-ingredient tomato"></div>
      <div className="ff-ingredient pepper"></div>
      <div className="ff-ingredient mushroom"></div>
      <div className="ff-ingredient leaf"></div>
      <div className="ff-ingredient onion"></div>
      <div className="ff-ingredient carrot"></div>
      <div className="ff-ingredient garlic"></div>
      <div className="ff-ingredient broccoli"></div>
      <div className="ff-ingredient banana"></div>
      <div className="ff-ingredient bread"></div>
      <div className="ff-ingredient egg"></div>
      <div className="ff-ingredient sausage"></div>
      <div className="ff-ingredient cheese"></div>
      <div className="ff-ingredient lemon"></div>
      <div className="ff-ingredient strawberry"></div>
    </div>
  );

  if (loading) {
    return (
      <div className="stripe-page loading" style={{ position: 'relative', overflow: 'hidden' }}>
        {floatingIngredients}
        <div className="stripe-card">
          <div className="loading-spinner"></div>
          <h1>Setting Up Payments</h1>
          <p>We're preparing your secure payment account. This will only take a moment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="stripe-page onboarding" style={{ position: 'relative', overflow: 'hidden' }}>
      {floatingIngredients}
      <div className="onboarding-header">
        <img src="/fodyfee_logo.png" alt="FodyFee" style={{ width: '60px', height: '60px' }} />
        <h1>Payment Setup</h1>
        <p>Securely connect your payment processor to start accepting orders.</p>
      </div>
      <div className="stripe-card" style={{ maxWidth: '480px' }}>
        <div className="pending-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
        </div>
        <h1>Redirecting to Stripe...</h1>
        <p>You will be securely redirected to Stripe to verify your business details.</p>
        {onboardingUrl && (
          <a href={onboardingUrl} className="primary-button" style={{ display: 'inline-block', textDecoration: 'none' }}>
            Continue to Stripe
          </a>
        )}
      </div>
      <div className="onboarding-footer">
        <p className="text-muted">
          Your information is securely processed by Stripe. FodyFee never stores your bank details.
        </p>
      </div>
    </div>
  );
}

export default OnboardingPage;
